import type { HomepagePageContent } from "./wave1-home";

const workflowHrefs = {
  automation: "/faceless-video-automation",
  script: "/script-to-faceless-video",
  url: "/url-to-faceless-video",
} as const;

const sample = (label: string, title: string, description: string) => ({
  label,
  title,
  description,
  transcript: description,
});

export const supplementalHomeContent = {
  nl: {
    metadata: {
      title: "AI-videogenerator voor gezichtsloze video's | AriaFlow",
      description:
        "Maak, controleer en publiceer automatisch gezichtsloze video's vanuit een idee, script of openbare URL.",
      keywords: [
        "AI gezichtsloze videogenerator",
        "gezichtsloze video's maken",
        "faceless video automatisering",
        "automatisch video's publiceren",
      ],
    },
    hero: {
      eyebrow: "AI-videoworkflows voor gezichtsloze kanalen",
      title: "Maak en publiceer automatisch gezichtsloze video's",
      description:
        "Begin met een idee, script of openbare URL. Controleer elk detail en publiceer volgens je planning, of automatiseer de workflow zodra die klaar is.",
      primary: "Begin gratis met je eerste workflow",
      secondary: "Bekijk echte voorbeelden",
      previewAlt:
        "AriaFlow-workflow voor het controleren en publiceren van gezichtsloze video's",
      caption:
        "Controleer vóór publicatie of automatiseer de workflow zodra die klaar is.",
    },
    workflowTypes: {
      title: "Begin met wat je al hebt",
      subtitle:
        "Gebruik een onderwerp, een afgerond script of een openbare URL en controleer het concept voordat je genereert of publiceert.",
      action: "Bekijk deze workflow",
      items: [
        {
          title: "Onderwerp naar gezichtsloze video",
          description:
            "Maak vanuit één duidelijk idee een contentplan en het eerste script.",
          output: "Voor nieuwe series en terugkerende onderwerpen",
          href: workflowHrefs.automation,
        },
        {
          title: "Script naar gezichtsloze video",
          description:
            "Behoud de goedgekeurde voice-over en verdeel de tekst over bewerkbare scènes.",
          output: "Voor makers en gecontroleerde content",
          href: workflowHrefs.script,
        },
        {
          title: "URL naar gezichtsloze video",
          description:
            "Zet een openbare bron om in een bewerkbaar concept met verhaal en beeld.",
          output: "Voor artikelen en uitlegvideo's",
          href: workflowHrefs.url,
        },
      ],
    },
    samples: {
      title: "Echte verticale video's gemaakt met AriaFlow",
      description:
        "Verhalen, dialogen, boekbesprekingen en langere afleveringen uit werkende AriaFlow-workflows. Vergelijk tempo, ondertiteling en beeldrichting.",
      cta: "Begin gratis",
      publicExample: "Bekijk openbaar voorbeeld",
      englishDisclosure: "Voorbeelden kunnen Engelse audio of ondertiteling bevatten.",
      secondShort: "sec",
      minuteShort: "min",
      items: {
        history: sample("Geschiedenis", "De nepstad die de bommen opving", "Een verticaal geschiedenisverhaal met terugkerende personages en voice-over."),
        scary: sample("Spannend verhaal", "Een kort spannend verhaal", "Een verticale vertelling met een directe opening, oplopende spanning en donkere beelden."),
        longForm: sample("Lange video", "Ochtendroutine tegen uitstelgedrag", "Een langere podcastvideo met twee stemmen, leesbare ondertiteling en een consistente stijl."),
        education: sample("URL naar video", "AriaFlow van URL naar video", "Een openbare pagina omgezet in een bewerkbare video met voice-over, ondertiteling en productscènes."),
        url: sample("URL naar video", "Overzicht van EnsembleData", "Een website samengevat in een korte video met een herschreven script, voice-over en ondertiteling."),
      },
    },
    process: {
      title: "Een complete workflow met controle vóór publicatie",
      description:
        "AriaFlow houdt renderen en toestemming om te publiceren apart, zodat kwaliteitscontrole zichtbaar blijft.",
      steps: [
        { title: "Invoer", description: "Begin met een onderwerp, goedgekeurd script of openbare URL." },
        { title: "Genereren", description: "Maak voice-over, scènes, beelden, ondertiteling, muziek en metadata." },
        { title: "Controleren", description: "Controleer script, stem, beelden, tempo, ondertiteling en kanaalinstellingen." },
        { title: "Plannen of publiceren", description: "Download de goedgekeurde video of publiceer die op het gekozen moment." },
      ],
      controlsTitle: "Houd controle over het concept",
      controls: [
        "Bewerk het gegenereerde script en scèneplan",
        "Kies stem, visuele stijl, beeldverhouding en tempo",
        "Maak verticale of horizontale video's tot 10 minuten",
        "Controleer metadata en het doelaccount",
      ],
      platformsEyebrow: "Gekoppeld publiceren",
      platformsTitle: "Publiceer waar je publiek kijkt",
      platformsDescription:
        "Koppel ondersteunde accounts, controleer bestemming en metadata en plan of publiceer daarna de goedgekeurde video.",
      platformsSupportedLabel: "Ondersteund",
      platformsDisclaimer:
        "Publiceren is afhankelijk van accounttoegang en de actuele API-regels van elk platform.",
      directoryTitle: "Workflows per invoer en platform",
      directoryDescription:
        "Kies de workflow die het best bij je video past. Elke pagina bevat praktische grenzen, een voorbeeld en uitleg.",
      directoryLabel: "Workflows voor gezichtsloze video's",
    },
    pricing: {
      title: "Prijzen voor jouw productietempo",
      subtitle:
        "Credits dekken script, beelden, stem en compositie. Bekijk eerst een geschatte bandbreedte.",
      view: "Bekijk prijzen en eenheidskosten",
      credits: "credits inbegrepen",
      estimate: "Geschatte video-output",
      disclaimer: "Model, stem, tempo, nieuwe pogingen en lengte bepalen het werkelijke verbruik.",
      perYear: "per jaar",
      perMonth: "per maand",
      minutes: "min",
      planDescriptions: {
        basic: "Om je eerste workflow te testen en consistente concepten te maken.",
        standard: "Voor makers die regelmatig meerdere gecontroleerde video's produceren.",
        pro: "Voor teams die meer volume nodig hebben in meerdere workflows.",
      },
    },
    faq: {
      title: "Veelgestelde vragen",
      description: "Lees meer over invoer, lengte, publiceren en gebruiksrechten voordat je begint.",
      items: [
        { question: "Wat is een AI-generator voor gezichtsloze video's?", answer: "Een workflow waarmee je video's maakt zonder zelf in beeld te komen. AriaFlow combineert script, AI-stem, beelden, ondertiteling, muziek en controle." },
        { question: "Kan ik mijn eigen script gebruiken?", answer: "Ja. Plak je script, controleer lengte en scènes en kies stem, stijl, ondertiteling, beeldverhouding en publicatie." },
        { question: "Kan AriaFlow een URL omzetten in video?", answer: "Ondersteunde openbare pagina's kunnen worden omgezet in een bewerkbaar videoconcept. Voor afgeschermde pagina's moet je mogelijk de tekst plakken." },
        { question: "Hoe lang kan een video zijn?", answer: "De huidige workflows ondersteunen video's tot 10 minuten. Verbruik en generatietijd hangen af van lengte, modellen en compositie." },
        { question: "Kan AriaFlow automatisch publiceren?", answer: "Ja. Koppel ondersteunde accounts voor YouTube, TikTok, Instagram en X en kies of je vóór publicatie controleert of de workflow automatiseert." },
        { question: "Ben ik eigenaar van de gemaakte video?", answer: "Het eindbestand is van jou, onder voorbehoud van je rechten op de bron en de voorwaarden van gebruikte modellen, media, muziek en stemmen." },
      ],
    },
    cta: {
      title: "Start gratis met je eerste gezichtsloze videoworkflow",
      description: "Begin met een idee, script of URL. Controleer het concept en publiceer handmatig of automatisch.",
      button: "Begin gratis",
    },
  },
  pl: {
    metadata: {
      title: "Generator filmów bez twarzy AI | AriaFlow",
      description: "Twórz, sprawdzaj i publikuj automatycznie filmy bez twarzy na podstawie pomysłu, scenariusza lub publicznego adresu URL.",
      keywords: ["generator filmów bez twarzy AI", "filmy bez twarzy", "automatyzacja filmów", "automatyczne publikowanie filmów"],
    },
    hero: {
      eyebrow: "Przepływy AI dla kanałów bez twarzy",
      title: "Twórz i publikuj filmy bez twarzy automatycznie",
      description: "Zacznij od pomysłu, scenariusza lub publicznego adresu URL. Sprawdź każdy szczegół, a potem publikuj według harmonogramu lub włącz automatyzację.",
      primary: "Uruchom pierwszy przepływ bezpłatnie",
      secondary: "Zobacz prawdziwe przykłady",
      previewAlt: "Przepływ AriaFlow do sprawdzania i publikowania filmów bez twarzy",
      caption: "Sprawdź film przed publikacją lub włącz automatyzację, gdy przepływ będzie gotowy.",
    },
    workflowTypes: {
      title: "Zacznij od tego, co już masz",
      subtitle: "Użyj tematu, gotowego scenariusza lub publicznego adresu URL i sprawdź szkic przed wygenerowaniem lub publikacją.",
      action: "Zobacz ten przepływ",
      items: [
        { title: "Temat na film bez twarzy", description: "Utwórz plan treści i pierwszy scenariusz na podstawie jednego pomysłu.", output: "Dla nowych serii i tematów cyklicznych", href: workflowHrefs.automation },
        { title: "Scenariusz na film bez twarzy", description: "Zachowaj zatwierdzoną narrację i podziel tekst na edytowalne sceny.", output: "Dla twórców i sprawdzonych treści", href: workflowHrefs.script },
        { title: "URL na film bez twarzy", description: "Zmień publiczne źródło w edytowalny szkic narracji i obrazu.", output: "Dla artykułów i filmów objaśniających", href: workflowHrefs.url },
      ],
    },
    samples: {
      title: "Prawdziwe pionowe filmy utworzone w AriaFlow",
      description: "Historie, dialogi, recenzje książek i dłuższe odcinki z działających przepływów AriaFlow. Porównaj tempo, napisy i kierunek wizualny.",
      cta: "Zacznij bezpłatnie",
      publicExample: "Zobacz publiczny przykład",
      englishDisclosure: "Przykłady mogą zawierać angielski dźwięk lub napisy.",
      secondShort: "s",
      minuteShort: "min",
      items: {
        history: sample("Historia", "Fałszywe miasto, które przyjęło bomby", "Pionowa opowieść historyczna z powracającymi postaciami i narracją."),
        scary: sample("Historia grozy", "Krótka opowieść z napięciem", "Pionowa narracja z bezpośrednim początkiem, rosnącym napięciem i mrocznymi obrazami."),
        longForm: sample("Długi film", "Poranna rutyna przeciw odkładaniu", "Dłuższy film podcastowy z dwoma głosami, czytelnymi napisami i spójnym stylem."),
        education: sample("URL na film", "AriaFlow: od URL do filmu", "Publiczna strona zamieniona w edytowalny film z narracją, napisami i scenami produktu."),
        url: sample("URL na film", "Przegląd EnsembleData", "Witryna skrócona do krótkiego filmu z przepisanym scenariuszem, narracją i napisami."),
      },
    },
    process: {
      title: "Pełny przepływ z kontrolą przed publikacją",
      description: "AriaFlow oddziela renderowanie od zgody na publikację, dzięki czemu kontrola jakości pozostaje widoczna.",
      steps: [
        { title: "Dane wejściowe", description: "Zacznij od tematu, zatwierdzonego scenariusza lub publicznego adresu URL." },
        { title: "Generowanie", description: "Przygotuj narrację, sceny, obrazy, napisy, muzykę i metadane." },
        { title: "Kontrola", description: "Sprawdź scenariusz, głos, obrazy, tempo, napisy i ustawienia kanału." },
        { title: "Planowanie lub publikacja", description: "Pobierz zatwierdzony film albo opublikuj go na połączonym kanale w wybranym czasie." },
      ],
      controlsTitle: "Zachowaj kontrolę nad szkicem",
      controls: ["Edytuj wygenerowany scenariusz i plan scen", "Wybierz głos, styl wizualny, proporcje i tempo", "Twórz pionowe lub poziome filmy do 10 minut", "Sprawdź metadane i konto docelowe"],
      platformsEyebrow: "Połączona publikacja",
      platformsTitle: "Publikuj tam, gdzie oglądają odbiorcy",
      platformsDescription: "Połącz obsługiwane konta, sprawdź miejsce docelowe i metadane, a następnie zaplanuj lub opublikuj zatwierdzony film.",
      platformsSupportedLabel: "Obsługiwane",
      platformsDisclaimer: "Publikowanie zależy od autoryzacji konta i aktualnych zasad API platformy.",
      directoryTitle: "Przepływy według źródła i platformy",
      directoryDescription: "Wybierz przepływ najbliższy filmowi, którego potrzebujesz. Każda strona zawiera ograniczenia, przykład i wskazówki.",
      directoryLabel: "Przepływy filmów bez twarzy",
    },
    pricing: {
      title: "Ceny dopasowane do tempa produkcji",
      subtitle: "Kredyty obejmują scenariusz, obrazy, głos i montaż. Przed wyborem sprawdź szacowany zakres.",
      view: "Zobacz ceny i koszty jednostkowe",
      credits: "kredytów w planie",
      estimate: "Szacowana liczba filmów",
      disclaimer: "Model, głos, tempo, ponowienia i długość wpływają na rzeczywiste zużycie.",
      perYear: "rocznie",
      perMonth: "miesięcznie",
      minutes: "min",
      planDescriptions: {
        basic: "Do przetestowania pierwszego przepływu i tworzenia spójnych szkiców.",
        standard: "Dla twórców regularnie produkujących kilka sprawdzonych filmów.",
        pro: "Dla zespołów potrzebujących większej skali w wielu przepływach.",
      },
    },
    faq: {
      title: "Często zadawane pytania",
      description: "Poznaj źródła, długość, publikowanie i prawa do treści przed utworzeniem pierwszego filmu.",
      items: [
        { question: "Czym jest generator filmów bez twarzy AI?", answer: "To przepływ do tworzenia filmów bez występowania przed kamerą. AriaFlow łączy scenariusz, głos AI, obrazy, napisy, muzykę i kontrolę." },
        { question: "Czy mogę użyć własnego scenariusza?", answer: "Tak. Wklej scenariusz, sprawdź długość i sceny oraz wybierz głos, styl, napisy, proporcje i sposób publikacji." },
        { question: "Czy AriaFlow zmienia URL w film?", answer: "Obsługiwane publiczne strony można przekształcić w edytowalny szkic filmu. W przypadku stron prywatnych może być konieczne wklejenie tekstu." },
        { question: "Jak długi może być film?", answer: "Obecne przepływy obsługują filmy do 10 minut. Zużycie i czas generowania zależą od długości, modeli i montażu." },
        { question: "Czy AriaFlow publikuje automatycznie?", answer: "Tak. Połącz obsługiwane konta YouTube, TikTok, Instagram i X, a następnie wybierz kontrolę przed publikacją lub automatyzację przepływu." },
        { question: "Czy utworzony film należy do mnie?", answer: "Plik końcowy należy do Ciebie, z uwzględnieniem praw do źródła i warunków modeli, mediów, muzyki oraz głosów." },
      ],
    },
    cta: {
      title: "Uruchom bezpłatnie pierwszy przepływ filmu bez twarzy",
      description: "Zacznij od pomysłu, scenariusza lub URL. Sprawdź szkic i publikuj ręcznie albo automatycznie.",
      button: "Zacznij bezpłatnie",
    },
  },
  ru: {
    metadata: {
      title: "ИИ-генератор видео без лица | AriaFlow",
      description: "Создавайте, проверяйте и автоматически публикуйте видео без лица по идее, сценарию или общедоступной ссылке.",
      keywords: ["ИИ-генератор видео без лица", "создать видео без лица", "автоматизация видео", "автопубликация видео"],
    },
    hero: {
      eyebrow: "ИИ-процессы для каналов без лица",
      title: "Создавайте и публикуйте видео без лица автоматически",
      description: "Начните с идеи, сценария или общедоступной ссылки. Проверьте каждую деталь, затем публикуйте по расписанию или включите автоматизацию.",
      primary: "Запустить первый процесс бесплатно",
      secondary: "Посмотреть реальные примеры",
      previewAlt: "Процесс AriaFlow для проверки и публикации видео без лица",
      caption: "Проверяйте видео перед публикацией или включайте автоматизацию, когда процесс готов.",
    },
    workflowTypes: {
      title: "Начните с того, что у вас уже есть",
      subtitle: "Используйте тему, готовый сценарий или общедоступную ссылку и проверьте черновик перед созданием или публикацией.",
      action: "Открыть этот процесс",
      items: [
        { title: "Тема в видео без лица", description: "Создайте контент-план и первый сценарий из одной четкой идеи.", output: "Для новых серий и регулярных тем", href: workflowHrefs.automation },
        { title: "Сценарий в видео без лица", description: "Сохраните утвержденный текст озвучки и разделите его на редактируемые сцены.", output: "Для авторов и проверенного контента", href: workflowHrefs.script },
        { title: "URL в видео без лица", description: "Преобразуйте общедоступный источник в редактируемый черновик с озвучкой и визуалом.", output: "Для статей и объясняющих видео", href: workflowHrefs.url },
      ],
    },
    samples: {
      title: "Реальные вертикальные видео, созданные в AriaFlow",
      description: "Истории, диалоги, книжные обзоры и длинные выпуски из работающих процессов AriaFlow. Сравните темп, субтитры и визуальный стиль.",
      cta: "Начать бесплатно",
      publicExample: "Открыть публичный пример",
      englishDisclosure: "Примеры могут содержать английскую озвучку или субтитры.",
      secondShort: "с",
      minuteShort: "мин",
      items: {
        history: sample("История", "Ложный город, принявший бомбовый удар", "Вертикальная историческая зарисовка с повторяющимися персонажами и озвучкой."),
        scary: sample("Страшная история", "Короткая история с напряжением", "Вертикальный рассказ с быстрым началом, нарастающим напряжением и мрачным визуалом."),
        longForm: sample("Длинное видео", "Утренняя рутина против прокрастинации", "Длинное подкаст-видео с двумя голосами, читаемыми субтитрами и единым стилем."),
        education: sample("URL в видео", "AriaFlow: из URL в видео", "Общедоступная страница превращена в редактируемое видео с озвучкой, субтитрами и сценами продукта."),
        url: sample("URL в видео", "Обзор EnsembleData", "Сайт сжат в короткое видео с переписанным сценарием, озвучкой и субтитрами."),
      },
    },
    process: {
      title: "Полный процесс с проверкой перед публикацией",
      description: "AriaFlow отделяет рендеринг от разрешения на публикацию, поэтому контроль качества всегда остается видимым.",
      steps: [
        { title: "Источник", description: "Начните с темы, утвержденного сценария или общедоступной ссылки." },
        { title: "Создание", description: "Подготовьте озвучку, сцены, изображения, субтитры, музыку и метаданные." },
        { title: "Проверка", description: "Проверьте сценарий, голос, изображения, темп, субтитры и настройки канала." },
        { title: "Расписание или публикация", description: "Скачайте утвержденное видео или отправьте его в подключенный канал в выбранное время." },
      ],
      controlsTitle: "Сохраняйте контроль над черновиком",
      controls: ["Редактируйте созданный сценарий и план сцен", "Выбирайте голос, визуальный стиль, формат и темп", "Создавайте вертикальные и горизонтальные видео до 10 минут", "Проверяйте метаданные и целевой аккаунт"],
      platformsEyebrow: "Публикация в подключенные каналы",
      platformsTitle: "Публикуйте там, где смотрит ваша аудитория",
      platformsDescription: "Подключите поддерживаемые аккаунты, проверьте площадку и метаданные, затем запланируйте или опубликуйте утвержденное видео.",
      platformsSupportedLabel: "Поддерживается",
      platformsDisclaimer: "Публикация зависит от авторизации аккаунта и текущих правил API платформы.",
      directoryTitle: "Процессы по источнику и платформе",
      directoryDescription: "Выберите процесс, который ближе всего к нужному видео. На каждой странице есть ограничения, пример и рекомендации.",
      directoryLabel: "Процессы для видео без лица",
    },
    pricing: {
      title: "Цены под ваш темп производства",
      subtitle: "Кредиты покрывают сценарий, изображения, голос и сборку. Перед выбором посмотрите примерный диапазон.",
      view: "Посмотреть цены и стоимость единиц",
      credits: "кредитов включено",
      estimate: "Примерный выпуск видео",
      disclaimer: "Модель, голос, темп, повторы и длина влияют на фактический расход.",
      perYear: "в год",
      perMonth: "в месяц",
      minutes: "мин",
      planDescriptions: {
        basic: "Для проверки первого процесса и создания стабильных черновиков.",
        standard: "Для авторов, которые регулярно выпускают несколько проверенных видео.",
        pro: "Для команд, которым нужен больший объем в нескольких процессах.",
      },
    },
    faq: {
      title: "Частые вопросы",
      description: "Узнайте об источниках, длине, публикации и правах на контент до создания первого видео.",
      items: [
        { question: "Что такое ИИ-генератор видео без лица?", answer: "Это процесс создания видео без появления в кадре. AriaFlow объединяет сценарий, ИИ-голос, изображения, субтитры, музыку и проверку." },
        { question: "Можно использовать свой сценарий?", answer: "Да. Вставьте сценарий, проверьте длину и сцены, выберите голос, стиль, субтитры, формат и способ публикации." },
        { question: "AriaFlow преобразует URL в видео?", answer: "Поддерживаемые общедоступные страницы можно превратить в редактируемый черновик видео. Для закрытых страниц может потребоваться вставить текст." },
        { question: "Какой может быть длина видео?", answer: "Текущие процессы поддерживают видео до 10 минут. Расход и время создания зависят от длины, моделей и сборки." },
        { question: "AriaFlow публикует автоматически?", answer: "Да. Подключите поддерживаемые аккаунты YouTube, TikTok, Instagram и X и выберите проверку перед публикацией или автоматический процесс." },
        { question: "Мне принадлежит созданное видео?", answer: "Финальный файл принадлежит вам с учетом прав на исходные материалы и условий используемых моделей, медиа, музыки и голосов." },
      ],
    },
    cta: {
      title: "Бесплатно запустите первый процесс создания видео без лица",
      description: "Начните с идеи, сценария или URL. Проверьте черновик и публикуйте вручную или автоматически.",
      button: "Начать бесплатно",
    },
  },
} satisfies Record<"nl" | "pl" | "ru", HomepagePageContent>;
